/**
 * TileMap strict validators. `layer_<i>/*` is a glued-index `PropertyListHelper`
 * family (tile_map.cpp:1023-1043), like TabBar's `tab_<i>/*`, of seven leaves plus
 * `tile_data`. See propertyListRouteCoverage.test.ts.
 */
import '../../../base/node2d/linterParser.js';
import { validatorRegistry } from '../../../../linter/ValidatorRegistry.js';
import { v } from '../../../../linter/validators/index.js';
import { indexedFamilyValidator } from '../../../../linter/validators/indexedFamily.js';
import { CANVAS_ITEM_Z_MAX, CANVAS_ITEM_Z_MIN } from '../../../../godot/rendering/rendering.js';
import { TILE_MAP_VISIBILITY_MODE_NAMES } from '../../../../godot/scene/tileMap.js';
import { formatValidator, tileDataValidator } from './tileDataSlots.js';
// Keyed by the shared name list, so a leaf declared in only one of the two
// places is a compile error.
const LAYER_LEAVES = {
    // tile_map.cpp:1030, Variant::STRING. set_layer_name (:333-335) forwards
    // straight to Node::set_name, no format constraint of its own.
    name: v.quotedString('name'),
    // tile_map.cpp:1031, Variant::BOOL.
    enabled: v.boolean('enabled'),
    // tile_map.cpp:1032, Variant::COLOR.
    modulate: v.color('modulate'),
    // tile_map.cpp:1033, Variant::BOOL.
    y_sort_enabled: v.boolean('y_sort_enabled'),
    // tile_map.cpp:1034, Variant::INT, PROPERTY_HINT_NONE "suffix:px". A plain
    // display origin: TileMapLayer::set_y_sort_origin (tile_map_layer.cpp:3321)
    // is a bare assignment past an equality early-out.
    y_sort_origin: v.int('y_sort_origin'),
    // tile_map.cpp:1035, Variant::INT. set_layer_z_index forwards to CanvasItem's
    // own z_index setter, whose ERR_FAIL_COND against CANVAS_ITEM_Z_MIN/MAX
    // (canvas_item.cpp:668) is enforced.
    z_index: v.int('z_index', {
        min: CANVAS_ITEM_Z_MIN,
        max: CANVAS_ITEM_Z_MAX,
        enforced: 'canvas_item.cpp:668',
    }),
    // tile_map.cpp:1037, Variant::BOOL, #ifndef NAVIGATION_2D_DISABLED (on by default).
    navigation_enabled: v.boolean('navigation_enabled'),
    // tile_map.cpp:1039: see tileDataSlots.ts.
    tile_data: tileDataValidator,
};
const layerValidator = indexedFamilyValidator({
    prefix: 'layer_',
    leaves: LAYER_LEAVES,
    unknownCode: 'INVALID_TILEMAP_LAYER_KEY',
    describes: 'layer',
    // PropertyListHelper's own `_get_property` returns nullptr unless the index
    // `is_valid_int()` (property_list_helper.cpp:53-55), so a non-numeric index
    // is a dropped write, as in TabBar's tab_<i>/* family.
    indexParse: 'is_valid_int',
    negativeIndex: {
        cite: 'property_list_helper.cpp:58',
        code: 'INVALID_TILEMAP_LAYER_INDEX',
        message: (index) => `Layer index ${index} must be non-negative. TileMap routes every layer_<idx>/<leaf> write through PropertyListHelper::_get_property, which returns nullptr for a negative index (property_list_helper.cpp:58), so the value is silently dropped`,
    },
});
validatorRegistry.registerAll('TileMap', {
    tile_set: v.resourceReference('tile_set'),
    // tile_map.cpp:747, PROPERTY_HINT_NONE with NO_EDITOR|INTERNAL usage: an
    // internal format-version tag Godot always writes. See tileDataSlots.ts.
    format: formatValidator,
    'layer_#/*': layerValidator,
    // tile_map.cpp:401-410, bare assignment (also toggles
    // notify_local_transform/physics_process_internal; no value constraint).
    collision_animatable: v.boolean('collision_animatable'),
    // tile_map.cpp:417-426, bare assignment, forwarded verbatim to every child
    // TileMapLayer::set_collision_visibility_mode. Hint at ADD_PROPERTY
    // tile_map.cpp:998.
    collision_visibility_mode: v.enumInt('collision_visibility_mode', TILE_MAP_VISIBILITY_MODE_NAMES, {
        hinted: 'tile_map.cpp:998',
    }),
    // tile_map.cpp:433-442, bare assignment, forwarded to every child layer.
    // Hint at ADD_PROPERTY tile_map.cpp:1000.
    navigation_visibility_mode: v.enumInt('navigation_visibility_mode', TILE_MAP_VISIBILITY_MODE_NAMES, {
        hinted: 'tile_map.cpp:1000',
    }),
    // tile_map.cpp:223-231: ERR_FAIL_COND_MSG(p_size < 1) floors it, and each child
    // layer's setter re-enforces it. No setter checks the hint's 128 ceiling
    // (tile_map.cpp:996), so it warns. TileMapLayer's own rendering_quadrant_size
    // has no RANGE hint (tilemaplayer/linterParser.ts).
    rendering_quadrant_size: v.int('rendering_quadrant_size', {
        min: 1,
        max: 128,
        enforced: { min: 'tile_map.cpp:224' },
        hinted: { max: 'tile_map.cpp:996' },
    }),
});
//# sourceMappingURL=linterParser.js.map